/**
 * Command menu (MiraOps-Usability-Designs/01, corrected): Ctrl/⌘K or the header button.
 * Searches companies, orders and vehicles the member can read in this workspace (the server
 * filters; hidden records are never counted) and lists places to go. Enter only opens: an
 * action that changes something still opens its own review screen; nothing is sent from
 * here. A native modal dialog keeps focus inside and returns it on close.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Generation, commandSearch, type CommandHit } from '@miraops/contracts';
import { transport } from '../lib/session.ts';
import { useDebounced } from '../lib/lists.tsx';
import { useI18n, type MessageKey } from '../lib/i18n.tsx';
import { Search } from '../ui/glyphs.tsx';
import { Spinner, wrapFocus } from '../ui/ui.tsx';

export type CommandPlace = 'today' | 'dispatch' | 'lookup' | 'bulkLookup' | 'duplicates' | 'routes' | 'activity' | 'runs' | 'sendHistory' | 'companies' | 'documents';
export type CommandTarget = { kind: 'company' | 'order'; id: string } | { kind: 'place'; place: CommandPlace };
interface Item { key: string; group: 'companies' | 'orders' | 'vehicles' | 'places'; label: string; detail: string | null; target: CommandTarget }

export function CommandMenu({ workspaceId, workspaceName, places, onSelect, onClose }: {
  workspaceId: string; workspaceName: string; places: CommandPlace[]; onSelect: (t: CommandTarget) => void; onClose: () => void;
}) {
  const { t } = useI18n();
  const dialog = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const q = useDebounced(text.trim(), 200);
  const [hits, setHits] = useState<CommandHit[]>([]);
  const [state, setState] = useState<'idle' | 'loading' | 'failed'>('idle');
  const [active, setActive] = useState(0);
  const gen = useRef(new Generation());
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const d = dialog.current;
    const opener = document.activeElement as HTMLElement | null;
    if (d && !d.open) { try { d.showModal(); } catch { d.setAttribute('open', ''); } }
    input.current?.focus();
    const onCancel = (e: Event) => { e.preventDefault(); closeRef.current(); };
    d?.addEventListener('cancel', onCancel);
    return () => { d?.removeEventListener('cancel', onCancel); if (d?.open) d.close(); if (opener?.isConnected) opener.focus(); };
  }, []);
  useEffect(() => {
    const ticket = gen.current.next();
    if (q.length < 2) { setHits([]); setState('idle'); return; }
    const abort = new AbortController();
    setState('loading');
    commandSearch(transport, workspaceId, q, abort.signal).then(
      r => { if (gen.current.current(ticket)) { setHits(r); setState('idle'); setActive(0); } },
      () => { if (gen.current.current(ticket) && !abort.signal.aborted) setState('failed'); });
    return () => abort.abort();
  }, [workspaceId, q]);
  const items = useMemo<Item[]>(() => {
    const needle = text.trim().toLocaleLowerCase();
    const placeItems = places.map(p => ({ key: `place:${p}`, group: 'places' as const, label: t(`cmd.place.${p}` as MessageKey), detail: t(`cmd.placeDetail.${p}` as MessageKey), target: { kind: 'place' as const, place: p } }))
      .filter(i => !needle || i.label.toLocaleLowerCase().includes(needle));
    const found = hits.map(h => ({ key: `${h.kind}:${h.id}`, group: h.kind === 'company' ? 'companies' as const : h.kind === 'order' ? 'orders' as const : 'vehicles' as const,
      label: h.label, detail: h.detail, target: h.kind === 'company' ? { kind: 'company' as const, id: h.id } : { kind: 'order' as const, id: h.order_id ?? h.id } }));
    return [...found, ...placeItems];
  }, [hits, places, text, t]);
  const choose = (i: Item | undefined) => { if (i) { onSelect(i.target); closeRef.current(); } };
  const onKey = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(items.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
    else if (e.key === 'Home' && e.ctrlKey) { e.preventDefault(); setActive(0); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(items[active]); }
  };
  useEffect(() => { document.getElementById(`cmd-${active}`)?.scrollIntoView({ block: 'nearest' }); }, [active]);
  const groups = ['companies', 'orders', 'vehicles', 'places'] as const;
  let index = -1;
  return (
    <dialog ref={dialog} className="cmd-layer" aria-label={t('cmd.title')} onClick={e => { if (e.target === dialog.current) closeRef.current(); }}>
      <div className="cmd" onKeyDown={e => { onKey(e); wrapFocus(e, e.currentTarget); }}>
        <div className="cmd-search"><Search size={20} />
          <input ref={input} className="cmd-input" value={text} maxLength={60} placeholder={t('cmd.placeholder')} aria-label={t('cmd.placeholder')}
            role="combobox" aria-expanded="true" aria-controls="cmd-list" aria-activedescendant={items.length ? `cmd-${active}` : undefined} aria-autocomplete="list"
            onChange={e => setText(e.target.value)} />
          <span className="muted small">{t('cmd.scope', { workspace: workspaceName })}</span>
        </div>
        <div id="cmd-list" role="listbox" aria-label={t('cmd.results')} className="cmd-list">
          {state === 'loading' && <div className="cmd-status"><Spinner label={t('state.loading')} /></div>}
          {state === 'failed' && <p className="cmd-status muted">{t('cmd.failed')}</p>}
          {q.length >= 2 && state === 'idle' && hits.length === 0 && <p className="cmd-status muted">{t('cmd.noRecords')}</p>}
          {groups.map(g => {
            const list = items.filter(i => i.group === g);
            if (!list.length) return null;
            return <div key={g} role="group" aria-label={t(`cmd.group.${g}` as MessageKey)}>
              <div className="cmd-group">{t(`cmd.group.${g}` as MessageKey)}</div>
              {list.map(i => { index++; const n = index; return (
                <div key={i.key} id={`cmd-${n}`} role="option" aria-selected={n === active} className={`cmd-item${n === active ? ' on' : ''}`}
                  onMouseMove={() => setActive(n)} onClick={() => choose(i)}>
                  <span><strong>{i.label}</strong>{i.detail && <span className="muted small">{i.detail}</span>}</span>
                  <span className="muted small">{t(`cmd.kind.${g}` as MessageKey)}</span>
                </div>); })}
            </div>;
          })}
        </div>
        <footer className="cmd-foot muted small"><span><kbd>↑</kbd><kbd>↓</kbd> {t('cmd.navigate')}</span><span><kbd>↵</kbd> {t('cmd.open')}</span><span><kbd>esc</kbd> {t('cmd.close')}</span>
          <span className="spacer" />{t('cmd.openOnly')}</footer>
      </div>
    </dialog>
  );
}
